/**
 * LivePostPicker.jsx
 * Choosing a post that is already live to be the advert's creative.
 *
 * The list comes from Meta rather than from this app's own history, so a post
 * put up directly on the Page or in the Instagram app is just as available as
 * one Kreeboom published. That is the whole point: the thing you want to
 * advertise is usually the thing that already did well, and it was not
 * necessarily posted from here.
 *
 * Facebook and Instagram are shown as separate lists rather than merged, and
 * one post can be chosen from each. That is not a nicety: two posts with
 * different captions are two different Meta objects and cannot be a single
 * creative, so covering both platforms means two ads — which this app then
 * puts inside one ad set, on one budget.
 *
 * Choosing from one list only is equally valid, and runs on that platform
 * alone. The section footer says which of the two is happening, because the
 * difference is a different advert and a different bill.
 *
 * Dependencies: services/api.js, components/Icons.jsx
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../../services/api.js';
import { Check, Refresh, Warning } from '../Icons.jsx';

export default function LivePostPicker({ brandId, selected, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await api.ads.livePosts(brandId));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) return <p className="mono">Reading your posts…</p>;

  if (error) {
    return (
      <div className="notice notice--error">
        <Warning />
        <span>{error.message}</span>
      </div>
    );
  }

  const groups = [
    { key: 'facebook', label: 'Facebook Page', posts: data?.facebook ?? [] },
    { key: 'instagram', label: 'Instagram', posts: data?.instagram ?? [] },
  ];
  const total = groups.reduce((n, g) => n + g.posts.length, 0);

  return (
    <div className="field">
      {data?.problem && (
        <div className="notice notice--error">
          <Warning />
          <span>{data.problem}</span>
        </div>
      )}

      {total === 0 && !data?.problem && (
        <p className="hint">
          Nothing live to advertise yet. Publish a post first, then it will appear here.
        </p>
      )}

      {groups.map((g) =>
        g.posts.length === 0 ? null : (
          <div key={g.key} style={{ marginBottom: 14 }}>
            <label>
              {g.label}
              <span className="counter">
                {selected?.[g.key] ? '1 chosen' : `${g.posts.length} to choose from`}
              </span>
            </label>

            <div className="live-posts">
              {g.posts.map((p) => {
                const isOn = selected?.[g.key]?.id === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`live-post${isOn ? ' is-on' : ''}`}
                    // One per platform: choosing another Facebook post replaces
                    // the Facebook choice and leaves Instagram alone.
                    onClick={() =>
                      onChange({ ...selected, [g.key]: isOn ? null : { ...p, platform: g.key } })
                    }
                  >
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt="" className="live-post__img" loading="lazy" />
                    ) : (
                      <div className="live-post__img live-post__img--empty">No picture</div>
                    )}

                    <div className="live-post__text">
                      <div className="live-post__caption">
                        {p.caption?.trim() ? p.caption.slice(0, 90) : 'No caption'}
                      </div>
                      <div className="live-post__date">
                        {p.publishedAt ? new Date(p.publishedAt).toLocaleDateString('en-GB') : ''}
                        {/* Meta's own answer, and the only reliable one. Shown
                            as a warning rather than used to hide the post,
                            because an absent value means "not stated". */}
                        {p.eligible === false ? ' · Meta says not promotable' : ''}
                      </div>
                    </div>

                    {isOn && (
                      <span className="live-post__tick">
                        <Check size={15} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ),
      )}

      <Chosen selected={selected} />

      <button className="btn btn--sm" onClick={load} disabled={loading} type="button">
        <Refresh /> {loading ? 'Reading…' : 'Refresh the list'}
      </button>
    </div>
  );
}

/**
 * What the choice adds up to.
 *
 * Spelled out because "two posts selected" and "two ads on one budget" are the
 * same fact stated at two different levels, and only the second one answers
 * what it will cost and how it will be paused.
 */
function Chosen({ selected }) {
  const fb = selected?.facebook;
  const ig = selected?.instagram;

  if (!fb && !ig) {
    return <p className="hint">Choose a post from either list — or one from each.</p>;
  }

  if (fb && ig) {
    return (
      <div className="notice notice--info">
        <Check />
        <span>
          <strong>One advert, two posts.</strong> Your Facebook post runs on Facebook and your
          Instagram post runs on Instagram, as two ads sharing a single budget, schedule and pause
          switch. Meta moves spend towards whichever is working.
        </span>
      </div>
    );
  }

  return (
    <p className="hint">
      Runs on {fb ? 'Facebook' : 'Instagram'} only. Pick a{' '}
      {fb ? 'post from the Instagram list' : 'Facebook post'} too and the advert covers both on one
      budget.
    </p>
  );
}
